import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  List, 
  ListItem, 
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Paper,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DeleteIcon from '@mui/icons-material/Delete';
import { uploadFiles, deleteFile } from '../services/blogService';

const FileUpload = ({ postId, attachments = [], setAttachments, disabled = false }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  
  const handleFileChange = async (e) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;
    
    setUploading(true);
    setError(null);
    
    try {
      // Upload the files
      const result = await uploadFiles(selectedFiles);
      
      // Add the new attachments to the existing ones
      setAttachments([...attachments, ...result.attachments]);
      
      // Reset the file input
      e.target.value = null;
    } catch (err) {
      console.error('Error uploading files:', err);
      setError('Failed to upload files. Please try again.');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };
  
  const handleDelete = async (index) => {
    if (!postId) {
      // If there's no postId, it's a new post, so just remove from the local state
      const newAttachments = [...attachments];
      newAttachments.splice(index, 1);
      setAttachments(newAttachments);
      return;
    }
    
    try {
      await deleteFile(postId, index);
      
      // Update the attachments list
      const newAttachments = [...attachments];
      newAttachments.splice(index, 1);
      setAttachments(newAttachments);
    } catch (err) {
      console.error('Error deleting file:', err);
      setError('Failed to delete file. Please try again.');
    }
  };
  
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' bytes';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) {
      return <ImageIcon />;
    } else if (fileType === 'application/pdf') {
      return <PictureAsPdfIcon />;
    } else {
      return <AttachFileIcon />;
    }
  };
  
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" gutterBottom>
        Attachments
      </Typography>
      
      <input
        type="file"
        multiple
        onChange={handleFileChange}
        accept="image/*,application/pdf"
        style={{ display: 'none' }}
        id="file-upload-input"
        disabled={disabled || uploading}
      />
      
      <label htmlFor="file-upload-input">
        <Button
          variant="outlined"
          component="span"
          startIcon={<CloudUploadIcon />}
          disabled={disabled || uploading}
          sx={{ mb: 2 }}
        >
          {uploading ? 'Uploading...' : 'Upload Files'}
        </Button>
      </label>
      
      {uploading && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, mb: 2 }}>
          <CircularProgress size={24} sx={{ mr: 1 }} />
          <Typography variant="body2">Uploading files...</Typography>
        </Box>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {attachments.length > 0 && (
        <Paper variant="outlined" sx={{ mt: 2 }}>
          <List dense>
            {attachments.map((file, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  {getFileIcon(file.fileType)}
                </ListItemIcon>
                <ListItemText
                  primary={file.fileName}
                  secondary={formatFileSize(file.fileSize)}
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Delete file">
                    <IconButton 
                      edge="end" 
                      aria-label="delete" 
                      onClick={() => handleDelete(index)}
                      disabled={disabled}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
      
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Allowed file types: Images, PDF • Max file size: 5MB • Max files: 5
      </Typography>
    </Box>
  );
};

export default FileUpload; 